import { MY_ACCOUNT_BASE_URL } from "@/constants";
import type { BannerContent } from "./global-banner.types";

/**
 * Gets the appropriate banner content based on current local time and pathname
 * @param now - Current date (defaults to new Date() for easier testing)
 * @param pathname - Current page pathname (optional, used for page-specific banners)
 * @returns Banner content configuration or null if no active promotion
 */
export function getBannerContent(
  now: Date = new Date(),
  pathname?: string,
): BannerContent | null {
  const year = now.getFullYear();
  const currentTime = now.getTime();

  const isInRange = (
    startMonth: number,
    startDay: number,
    startHour: number,
    startMinute: number,
    endMonth: number,
    endDay: number,
    endHour: number,
    endMinute: number,
  ) => {
    const start = new Date(year, startMonth, startDay, startHour, startMinute);
    const end = new Date(year, endMonth, endDay, endHour, endMinute);

    return currentTime >= start.getTime() && currentTime <= end.getTime();
  };

  const isWishlistPage = pathname?.includes("/wishlist");

  // Hide banner on discover sub-pages to avoid content overlap with nav
  // Pattern: /gb/topshop/discover/denim/loose, /gb/topman/discover/denim/skinny, etc.
  const isDiscoverSubPage =
    /^\/(gb\/)?(topshop|topman)\/discover\/[^/]+\/.+/.test(pathname ?? "");
  if (isDiscoverSubPage) {
    return null;
  }

  // Wishlist Perks: 19th Jan 00:00 - 19th Feb 23:59 (wishlist page only)
  const isWishlistPerksPeriod = isInRange(0, 19, 0, 0, 1, 19, 23, 59);

  // Next Day Delivery: 27th Jan 00:00 - 28th Jan 23:59
  const isNextDayDeliveryPeriod = isInRange(0, 27, 0, 0, 0, 28, 23, 59);

  // Join the List: 29th Jan 23:59 - 6th Feb 23:59
  const isJoinTheListPeriod = isInRange(0, 29, 23, 59, 1, 6, 23, 59);

  // Denim Fit Guide: 7th Feb 00:00 - 14th Feb 23:59
  const isDenimFitGuidePeriod = isInRange(1, 7, 0, 0, 1, 14, 23, 59);

  // Wishlist-specific banner takes priority on wishlist page
  if (isWishlistPage && isWishlistPerksPeriod) {
    return {
      title: "",
      subtitle:
        "ORDERS OVER £40 - FREE STANDARD DELIVERY (NEXT DAY DELIVERY ONLY £2.95)",
      text: "",
      code: "",
      colorScheme: "black",
      clickable: false,
    };
  }

  if (isNextDayDeliveryPeriod) {
    return {
      title: "",
      subtitle: "NEXT DAY DELIVERY ONLY £2.95 (ORDERS OVER £40)",
      text: "",
      code: "",
      colorScheme: "black",
      clickable: false,
    };
  }

  if (isJoinTheListPeriod) {
    return {
      title: "",
      subtitle:
        "JOIN THE LIST EVERYONE IS TALKING ABOUT - SIGN UP FOR EXCLUSIVE NEWS & BENEFITS",
      text: "",
      code: "",
      colorScheme: "black",
      clickable: true,
      href: MY_ACCOUNT_BASE_URL,
    };
  }

  if (isDenimFitGuidePeriod) {
    return {
      title: "",
      subtitle:
        "FIND YOUR PERFECT MATCH - EXPLORE THE DENIM FIT GUIDE AND SHOP LATEST STYLES",
      text: "",
      code: "",
      colorScheme: "black",
      clickable: true,
      href: "/topshop/discover/denim",
    };
  }

  return null;
}
